import { createCharacterPresentation } from '../lib/character_presentation.js';
import { Controller } from '@hotwired/stimulus';
import { waitForRoundAnimation } from '../lib/round_animation.js';

const PHASE_ATTRIBUTE = 'data-character-reveal-phase';

// A presentation-only copy flies above the screen while the real, morphable card
// stays in the roster. Only an existing hidden card becoming active triggers it.
export default class extends Controller {
  static targets = ['presentations'];

  connect() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.cancelOnMotionChange = () => this.cancelAndCenter();
    this.reducedMotion.addEventListener('change', this.cancelOnMotionChange);
  }

  beforeMorph(event) {
    if (this.element?.getAttribute('data-game-screen--character-layout-combat-value') === 'false') return;
    const character = event.target;
    const incoming = event.detail.newElement;
    if (!incoming) {
      if (this.reveal && character.contains(this.reveal.character)) this.cancel();
      return;
    }
    if (!character.matches('.character_wrapper')) return;

    const becomingActive = incoming.classList.contains('active_turn') &&
      !incoming.classList.contains('hidden_character');

    if (this.reveal?.character === character) {
      if (!becomingActive) this.cancel();
      return;
    }

    if (!becomingActive || !character.classList.contains('hidden_character') || this.reducedMotion.matches) return;

    this.cancel();
    this.reveal = { character, timers: new Map(), animations: new Set() };
    character.setAttribute(PHASE_ATTRIBUTE, 'waiting');
  }

  preservePhase(event) {
    if (event.target === this.reveal?.character && event.detail.attributeName === PHASE_ATTRIBUTE) {
      event.preventDefault();
    }
  }

  afterMorph(event) {
    const reveal = this.reveal;
    if (event.target !== reveal?.character || reveal.started) return;
    reveal.started = true;
    // Finish the enclosing Turbo morph before adding the temporary presentation.
    queueMicrotask(() => {
      if (this.reveal === reveal) this.play(reveal);
    });
  }

  async play(reveal) {
    try {
      await waitForRoundAnimation(reveal.character);
      if (this.reveal !== reveal || !reveal.character.isConnected) return;
      this.dispatch('before-presentation', { detail: { character: reveal.character } });
      reveal.overlay = this.createOverlay(reveal.character);
      await this.animate(reveal, [
        { transform: 'translate(calc(50vw + 100%), -50%) rotate(8deg)', opacity: 0 },
        { transform: 'translate(-50%, -50%) rotate(0deg)', opacity: 1 }
      ], 650);
      if (this.reveal !== reveal) return;

      await this.wait(reveal, 3000);
      if (this.reveal !== reveal) return;
      reveal.character.setAttribute(PHASE_ATTRIBUTE, 'space');
      const space = reveal.character.animate([
        { maxWidth: '0px' },
        { maxWidth: `${reveal.character.offsetWidth}px` }
      ], { duration: 650, easing: 'ease-in-out', fill: 'forwards' });
      reveal.animations.add(space);
      await space.finished;
      if (this.reveal !== reveal) return;
      reveal.character.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });

      await this.wait(reveal, 350);
      if (this.reveal !== reveal) return;
      const card = reveal.character.querySelector('.character.card');
      const destination = card.getBoundingClientRect();
      const origin = reveal.overlay.getBoundingClientRect();
      if (!destination.width || !destination.height) return;

      // Bounding rectangles include screen rotation; rotate the flying card to
      // match the destination before handing back to the real card.
      const rotation = this.rotation(reveal.character);
      const quarterTurn = Math.abs(rotation % 180) === 90;
      const width = quarterTurn ? destination.height : destination.width;
      const height = quarterTurn ? destination.width : destination.height;
      const x = destination.left + destination.width / 2 - window.innerWidth / 2;
      const y = destination.top + destination.height / 2 - window.innerHeight / 2;
      await this.animate(reveal, [
        { transform: 'translate(-50%, -50%) rotate(0deg) scale(1)' },
        { transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) rotate(${rotation}deg) scale(${width / origin.width}, ${height / origin.height})` }
      ], 800);
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Character reveal failed', error);
    } finally {
      if (this.reveal === reveal) this.cancel();
    }
  }

  createOverlay(character) {
    return createCharacterPresentation(character, this.presentationsTarget);
  }

  rotation(character) {
    let rotation = 0;
    for (let element = character; element; element = element.parentElement) {
      const transform = getComputedStyle(element).transform;
      if (transform === 'none') continue;
      const matrix = new DOMMatrixReadOnly(transform);
      rotation += Math.atan2(matrix.b, matrix.a) * 180 / Math.PI;
    }
    return Math.round(rotation);
  }

  animate(reveal, keyframes, duration) {
    const animation = reveal.overlay.animate(keyframes, {
      duration, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards'
    });
    reveal.animations.add(animation);
    return animation.finished;
  }

  wait(reveal, duration) {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        reveal.timers.delete(timer);
        resolve();
      }, duration);
      reveal.timers.set(timer, resolve);
    });
  }

  cancelAndCenter() {
    const character = this.reveal?.character;
    this.cancel();
    if (character?.isConnected && character.classList.contains('active_turn') &&
        !character.classList.contains('hidden_character')) {
      character.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'center' });
    }
  }

  cancel() {
    const reveal = this.reveal;
    if (!reveal) return;
    this.reveal = null;
    for (const [timer, resolve] of reveal.timers) {
      clearTimeout(timer);
      resolve();
    }
    for (const animation of reveal.animations) animation.cancel();
    reveal.overlay?.remove();
    reveal.character.removeAttribute(PHASE_ATTRIBUTE);
  }

  disconnect() {
    this.cancel();
    this.reducedMotion.removeEventListener('change', this.cancelOnMotionChange);
  }
}
